import React, { useState, useEffect } from 'react';
import { ArrowDown, Download, Github, Globe, Sparkles, Code, Zap } from 'lucide-react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Button } from '../ui/button';

const Hero = ({ data }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  const scale = useTransform(scrollY, [0, 300], [1, 0.8]);
  const springConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };
  const x = useSpring(0, springConfig);
  const ySpring = useSpring(0, springConfig);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const xPct = (clientX / innerWidth - 0.5) * 2;
      const yPct = (clientY / innerHeight - 0.5) * 2;
      x.set(xPct * 20);
      ySpring.set(yPct * 20);
      setMousePosition({ x: clientX, y: clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [x, ySpring]);

  const scrollToAbout = () => {
    document.querySelector('#about')?.scrollIntoView({ behavior: 'smooth' });
  };

  const floatingIcons = [
    { icon: Code, delay: 0, x: '10%', y: '20%' },
    { icon: Sparkles, delay: 0.5, x: '80%', y: '30%' },
    { icon: Zap, delay: 1, x: '15%', y: '70%' },
    { icon: Github, delay: 1.5, x: '85%', y: '60%' },
  ];

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-20 overflow-hidden">
      {/* Subtle Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/5 rounded-full blur-3xl" />
      </div>
      
      <motion.div 
        className="max-w-5xl mx-auto text-center relative z-10"
        style={{ y, opacity, scale }}
      >
        <motion.div 
          className="space-y-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          {/* Profile Image */}
          <div className="flex justify-center mb-10">
            <div className="relative">
              <div className="w-32 h-32 sm:w-36 sm:h-36 lg:w-40 lg:h-40 rounded-full overflow-hidden border-2 border-gray-700 bg-gray-800/50">
                <img
                  src="/Dwayne.png"
                  alt={`${data.name} - Profile`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </div>
          </div>

          {/* Name and Title */}
          <div className="space-y-6 mb-8">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white tracking-tight">
              {data.name}
            </h1>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-xl sm:text-2xl">
              <span className="text-emerald-400 font-semibold">
                {data.title}
              </span>
              <span className="hidden sm:block text-gray-600">•</span>
              <span className="text-gray-400">
                {data.subtitle}
              </span>
            </div>
          </div>

          {/* Tagline */}
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-10">
            {data.tagline}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={scrollToAbout}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-3 text-base font-semibold rounded-lg transition-colors"
            >
              View My Work
              <ArrowDown className="h-4 w-4 ml-2" />
            </Button>
            
            <Button
              variant="outline"
              className="border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white px-8 py-3 text-base font-semibold rounded-lg transition-colors"
              onClick={() => window.open(data.resumeUrl, '_blank')}
            >
              <Download className="h-4 w-4 mr-2" />
              Download Resume
            </Button>
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2, duration: 0.8 }}
        >
          <motion.button
            onClick={scrollToAbout}
            className="text-gray-500 hover:text-emerald-400 transition-colors duration-300 p-2 rounded-full hover:bg-emerald-400/10"
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
          >
            <ArrowDown size={24} />
          </motion.button>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;